/**
 * Symbol chart-time metadata: turns the backend's `ChartTime` enum values
 * (Symbol.allowedChartTimes / Symbol.defaultChartTime) into the interval codes
 * the chart API and the interval selector speak ('1m', '5m', '1h', …).
 *
 * Both fields are optional on a Symbol — older records and symbols that were
 * never configured leave them null. Every helper here falls back to the
 * built-in defaults in that case, so an unconfigured symbol behaves exactly as
 * it did before the fields existed.
 */

/** Interval codes accepted by ChartInterval.parse() on the backend, in order. */
export const ALL_INTERVALS = ['1m', '5m', '15m', '1h', '4h', '1d', '1w', '1M']

/**
 * Shown when a symbol carries no allowedChartTimes — the selector's original
 * hard-coded list.
 */
export const DEFAULT_INTERVALS = ['5m', '1h', '1d', '1w', '1M']

/** Interval used when neither the symbol nor the caller supplies one. */
export const FALLBACK_INTERVAL = '5m'

/**
 * ChartTime enum name -> interval code. M30 and Y1 exist in the enum but have
 * no candle table behind them (see ChartInterval), so they're intentionally
 * absent and get dropped rather than producing a button that 400s.
 */
const CHART_TIME_TO_INTERVAL = {
  M1:  '1m',
  M5:  '5m',
  M15: '15m',
  H1:  '1h',
  H4:  '4h',
  D1:  '1d',
  W1:  '1w',
  MN1: '1M',
}

/**
 * Normalise one ChartTime value to an interval code.
 * Accepts the enum name ('H1'), and tolerates a value that is already an
 * interval code ('1h') in case the API shape changes.
 * @returns {string|null} null when the value is unknown or unsupported.
 */
export function toInterval(chartTime) {
  if (!chartTime) return null
  const raw = String(chartTime).trim()
  const mapped = CHART_TIME_TO_INTERVAL[raw.toUpperCase()]
  if (mapped) return mapped
  return ALL_INTERVALS.includes(raw) ? raw : null
}

/**
 * The interval codes a symbol allows, ordered shortest-first.
 * @param {object|null} symbol Symbol/SymbolBasic record.
 * @returns {string[]} DEFAULT_INTERVALS when the symbol has no usable list.
 */
export function allowedIntervals(symbol) {
  const times = symbol && symbol.allowedChartTimes
  if (!Array.isArray(times) || !times.length) return DEFAULT_INTERVALS

  const codes = times.map(toInterval).filter(Boolean)
  const unique = ALL_INTERVALS.filter(i => codes.includes(i))
  // Everything the symbol listed was unknown/unsupported — don't leave the
  // selector empty.
  return unique.length ? unique : DEFAULT_INTERVALS
}

/**
 * The interval to select for a symbol: its defaultChartTime when set and
 * allowed, otherwise the currently selected interval if the symbol still
 * allows it, otherwise the shortest allowed one.
 *
 * @param {object|null} symbol  Symbol/SymbolBasic record.
 * @param {string} [current]    Interval currently shown, kept when still valid.
 */
export function defaultInterval(symbol, current) {
  const allowed = allowedIntervals(symbol)
  const preferred = toInterval(symbol && symbol.defaultChartTime)
  if (preferred && allowed.includes(preferred)) return preferred
  if (current && allowed.includes(current)) return current
  return allowed.includes(FALLBACK_INTERVAL) ? FALLBACK_INTERVAL : allowed[0]
}
